import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { serverAPI, ServerRecord, adminAPI } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const AdminServers: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [servers, setServers] = useState<ServerRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');

    // Modal states for Electron compatibility (window.prompt/confirm not supported)
    const [featuredModal, setFeaturedModal] = useState<{ open: boolean; serverId: string | null; days: string }>({
        open: false,
        serverId: null,
        days: '7'
    });
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; serverId: string | null }>({
        open: false,
        serverId: null
    });
    const [removeFeaturedModal, setRemoveFeaturedModal] = useState<{ open: boolean; serverId: string | null }>({
        open: false,
        serverId: null
    });

    const fetchServers = async () => {
        try {
            setLoading(true);
            const response = await serverAPI.getAllServers({ page, limit: 10, search });
            setServers(response.data);
            setTotalPages(response.meta.totalPages);
        } catch (err) {
            setError(t('common.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchServers();
        }, 500); // Debounce search
        return () => clearTimeout(timeoutId);
    }, [page, search]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1); // Reset to first page on search
    };

    const handleDelete = async () => {
        if (!deleteModal.serverId) return;
        try {
            await serverAPI.deleteServer(deleteModal.serverId);
            setDeleteModal({ open: false, serverId: null });
            fetchServers();
        } catch (err) {
            console.error('Failed to delete server:', err);
            setError(t('common.error'));
        }
    };

    const handleSetFeatured = async () => {
        if (!featuredModal.serverId) return;
        const days = parseInt(featuredModal.days);
        if (isNaN(days) || days <= 0) {
            setError(t('validation.invalid_number') || 'Invalid number');
            return;
        }
        try {
            await adminAPI.setFeatured(featuredModal.serverId, days);
            setFeaturedModal({ open: false, serverId: null, days: '7' });
            fetchServers();
        } catch (err) {
            console.error(err);
            setError(t('common.error'));
        }
    };

    const handleRemoveFeatured = async () => {
        if (!removeFeaturedModal.serverId) return;
        try {
            await adminAPI.removeFeatured(removeFeaturedModal.serverId);
            setRemoveFeaturedModal({ open: false, serverId: null });
            fetchServers();
        } catch (err) {
            console.error(err);
            setError(t('common.error'));
        }
    };

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                    {t('admin.servers')}
                </h1>
                <div className="flex gap-4">
                    <button
                        onClick={() => navigate('/admin/servers/new')}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('admin.add_new_server')}
                    </button>
                    <button
                        onClick={() => navigate('/admin')}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        {t('admin.back_to_dashboard')}
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder={t('home.search_placeholder')}
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full md:w-1/3 bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-blue-500"
                />
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">{t('admin.server_form.server_name')}</th>
                                <th className="px-6 py-4">{t('admin.server_form.game')}</th>
                                <th className="px-6 py-4">{t('admin.server_form.status')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                                        {t('common.loading')}
                                    </td>
                                </tr>
                            ) : servers.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                                        {t('server.list.empty_title')}
                                    </td>
                                </tr>
                            ) : (
                                servers.map((server) => (
                                    <tr key={server.id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-white">{server.name}</div>
                                            <div className="text-sm text-gray-400 truncate max-w-xs">{server.shortDescription}</div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-300">
                                            {server.game}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${server.status === 'online' ? 'bg-green-900/50 text-green-400' :
                                                server.status === 'offline' ? 'bg-red-900/50 text-red-400' :
                                                    'bg-yellow-900/50 text-yellow-400'
                                                }`}>
                                                {t(`server.status.${server.status}`)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {server.featuredUntil && new Date(server.featuredUntil) > new Date() ? (
                                                <button
                                                    onClick={() => setRemoveFeaturedModal({ open: true, serverId: server.id })}
                                                    className="text-orange-400 hover:text-orange-300 mr-3 transition-colors"
                                                >
                                                    {t('admin.servers.remove_featured') || 'Remove Featured'}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => setFeaturedModal({ open: true, serverId: server.id, days: '7' })}
                                                    className="text-yellow-400 hover:text-yellow-300 mr-3 transition-colors"
                                                >
                                                    {t('admin.servers.set_featured')}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => navigate(`/servers/${server.id}/edit`)}
                                                className="text-blue-400 hover:text-blue-300 mr-3 transition-colors"
                                            >
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                onClick={() => setDeleteModal({ open: true, serverId: server.id })}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                {t('common.delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-700 flex justify-between items-center">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className={`px-3 py-1 rounded ${page === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
                        >
                            {t('common.previous')}
                        </button>
                        <span className="text-gray-400">
                            {t('common.page_info').replace('{0}', page.toString()).replace('{1}', totalPages.toString())}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className={`px-3 py-1 rounded ${page === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
                        >
                            {t('common.next')}
                        </button>
                    </div>
                )}
            </div>

            {/* Featured Modal */}
            {featuredModal.open && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
                        <h3 className="text-xl font-bold text-white mb-4">{t('admin.servers.set_featured')}</h3>
                        <p className="text-gray-300 mb-4">{t('admin.servers.featured_duration_prompt') || 'Enter number of days to feature this server:'}</p>
                        <input
                            type="number"
                            min="1"
                            value={featuredModal.days}
                            onChange={(e) => setFeaturedModal(prev => ({ ...prev, days: e.target.value }))}
                            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white mb-4"
                            autoFocus
                        />
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setFeaturedModal({ open: false, serverId: null, days: '7' })}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleSetFeatured}
                                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg"
                            >
                                {t('common.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Featured Modal */}
            {removeFeaturedModal.open && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
                        <h3 className="text-xl font-bold text-white mb-4">{t('admin.servers.remove_featured')}</h3>
                        <p className="text-gray-300 mb-4">{t('admin.servers.confirm_remove_featured') || 'Are you sure you want to remove this server from featured?'}</p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setRemoveFeaturedModal({ open: false, serverId: null })}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleRemoveFeatured}
                                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg"
                            >
                                {t('common.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {deleteModal.open && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
                        <h3 className="text-xl font-bold text-white mb-4">{t('common.delete')}</h3>
                        <p className="text-gray-300 mb-4">{t('admin.comments.delete_confirm') || 'Are you sure you want to delete this item?'}</p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setDeleteModal({ open: false, serverId: null })}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg"
                            >
                                {t('common.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminServers;

